import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import GoogleOAuthButton from '../../components/GoogleOAuthButton';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, studentLogin, staffLogin } = useAuth();
  const [loginType, setLoginType] = useState('student'); // 'student' or 'staff'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [formData, setFormData] = useState({
    collegeEmail: '',
    password: ''
  });

  // Handle OAuth errors from URL params
  useEffect(() => {
    const error = searchParams.get('error');
    const message = searchParams.get('message');
    
    if (error && message) {
      toast.error(decodeURIComponent(message));
      // Clean up URL
      navigate('/login', { replace: true });
    }
  }, [searchParams, navigate]);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Auto-complete email domain
  const handleEmailChange = (value) => {
    setFormData({ ...formData, collegeEmail: value });
  };

  // Get full email for submission
  const getFullEmail = (email) => {
    if (!email.includes('@')) {
      // Auto-append domain if only username provided
      return email + '.nc@cambridge.edu.in';
    }
    // Return the full email as entered
    return email;
  };

  // Student Password Login
  const handleStudentLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);

    try {
      const fullEmail = getFullEmail(formData.collegeEmail.trim());
      console.log('🔐 Attempting student login with:', fullEmail);
      await studentLogin(fullEmail, formData.password.trim());
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Student login error:', error);
      
      // Check for email verification required
      if (error.response?.data?.requiresVerification) {
        navigate('/verify-email', { 
          state: { 
            email: error.response.data.email,
            message: error.response.data.message,
            requiresVerification: true
          }
        });
        return;
      }
      
      // Check for network errors (no response from server)
      if (!error.response) {
        toast.error('❌ Cannot connect to server. Please check your internet connection or try again later.');
      } else if (error.response?.status === 503) {
        // Database connection error
        toast.error('❌ Database connection error. The server cannot reach the database. Please try again later.');
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Handle validation errors
        error.response.data.errors.forEach(err => {
          toast.error(err.msg || err.message || 'Validation error');
        });
      } else {
        // Handle specific error messages
        const errorMessage = error.response?.data?.message || error.response?.data?.error || '';
        
        if (errorMessage.toLowerCase().includes('user not found') || 
            errorMessage.toLowerCase().includes('no user found') ||
            errorMessage.toLowerCase().includes('does not exist')) {
          toast.error('❌ Account not found. Please register first or check your email.');
          setTimeout(() => {
            if (window.confirm('Would you like to register a new account?')) {
              navigate('/register');
            }
          }, 2000);
        } else if (errorMessage.toLowerCase().includes('invalid password') ||
                   errorMessage.toLowerCase().includes('incorrect password') ||
                   errorMessage.toLowerCase().includes('wrong password')) {
          toast.error('❌ Incorrect password. Please try again.');
        } else if (errorMessage.toLowerCase().includes('invalid credentials')) {
          toast.error('❌ Invalid email or password. Please check and try again.');
        } else {
          toast.error(errorMessage || 'Login failed. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Staff Password Login
  const handleStaffLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);

    try {
      const fullEmail = getFullEmail(formData.collegeEmail.trim());
      console.log('🔐 Attempting staff login with:', fullEmail);
      await staffLogin(fullEmail, formData.password.trim());
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Staff login error:', error);
      
      // Check for network errors (no response from server)
      if (!error.response) {
        toast.error('❌ Cannot connect to server. Please check your internet connection or try again later.');
      } else if (error.response?.status === 503) {
        // Database connection error
        toast.error('❌ Database connection error. The server cannot reach the database. Please try again later.');
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Handle validation errors
        error.response.data.errors.forEach(err => {
          toast.error(err.msg || err.message || 'Validation error');
        });
      } else {
        // Handle specific error messages
        const errorMessage = error.response?.data?.message || error.response?.data?.error || '';
        
        if (errorMessage.toLowerCase().includes('staff not found') || 
            errorMessage.toLowerCase().includes('no staff found') ||
            errorMessage.toLowerCase().includes('does not exist')) {
          toast.error('❌ Staff account not found. Please contact administrator.');
        } else if (errorMessage.toLowerCase().includes('invalid password') ||
                   errorMessage.toLowerCase().includes('incorrect password') ||
                   errorMessage.toLowerCase().includes('wrong password')) {
          toast.error('❌ Incorrect password. Please try again.');
        } else if (errorMessage.toLowerCase().includes('invalid credentials')) {
          toast.error('❌ Invalid email or password. Please check and try again.');
        } else {
          toast.error(errorMessage || 'Login failed. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🎓</div>
            <h2 className="text-3xl font-bold text-slate-900">Welcome Back</h2>
            <p className="text-slate-600 mt-2">Sign in to your CITNC ERP account</p>
          </div>

          {/* Login Type Tabs */}
          <div className="flex gap-2 mb-6 bg-slate-100 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => {
                setLoginType('student');
                setFormData({ collegeEmail: '', password: '' });
              }}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                loginType === 'student'
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              👨‍🎓 Student
            </button>
            <button
              type="button"
              onClick={() => {
                setLoginType('staff');
                setFormData({ collegeEmail: '', password: '' });
              }}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                loginType === 'staff'
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              👨‍💼 Staff
            </button>
          </div>

          {/* Staff Login Form */}
          {loginType === 'staff' && (
            <form onSubmit={handleStaffLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  College Email *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    placeholder="library.nc@cambridge.edu.in or library"
                    value={formData.collegeEmail}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                  />
                  {!formData.collegeEmail.includes('@') && formData.collegeEmail.length > 0 && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-slate-400">
                      .nc@cambridge.edu.in
                    </div>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Enter your full college email or just the username
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:text-primary-dark transition-colors"
                >
                  Forgot Password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="spinner border-2 border-white border-t-transparent w-5 h-5"></div>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          )}

          {/* Student Login Form */}
          {loginType === 'student' && (
            <div className="space-y-4">
              {/* Google OAuth Button - Primary */}
              <GoogleOAuthButton type="login" />

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-slate-500">OR</span>
                </div>
              </div>

              {/* Toggle Password Form Button */}
              <button
                type="button"
                onClick={() => setShowPasswordForm(!showPasswordForm)}
                className="w-full py-3 px-4 border-2 border-slate-300 text-slate-700 rounded-lg font-medium hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                Sign in with Password
                <svg 
                  className={`w-4 h-4 transition-transform ${showPasswordForm ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Collapsible Password Login Form */}
              {showPasswordForm && (
                <div className="animate-fadeIn">
                  <form onSubmit={handleStudentLogin} className="space-y-4 pt-2">
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  College Email *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    placeholder="gowrish.cs@cambridge.edu.in or gowrish.cs"
                    value={formData.collegeEmail}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                  />
                  {!formData.collegeEmail.includes('@') && formData.collegeEmail.length > 0 && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-slate-400">
                      .nc@cambridge.edu.in
                    </div>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Enter your full college email or just the username
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:text-primary-dark transition-colors"
                >
                  Forgot Password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="spinner border-2 border-white border-t-transparent w-5 h-5"></div>
                    Signing in...
                  </span>
                ) : (
                  'Sign In with Password'
                )}
              </button>
              </form>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              {loginType === 'student' ? "Don't have an account?" : 'Students only'}{' '}
              {loginType === 'student' && (
                <Link to="/register" className="text-primary hover:text-primary-dark font-medium">
                  Register now
                </Link>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
